import { useState, useEffect } from "react";
import { 
  FaTimes, 
  FaPlus, 
  FaTrash, 
  FaUpload, 
  FaTag, 
  FaFileAlt, 
  FaBolt, 
  FaImage,
  FaListAlt,
  FaCog,
  FaEdit,
  FaPlusCircle
} from "react-icons/fa";
import { useProducts } from "./ProductContext";
import "../ProductFormModal.css";

function ProductFormModal({ isOpen, onClose, editProductId = null }) {
  const { addProduct, updateProduct, getProductById } = useProducts();
  const [isEditing, setIsEditing] = useState(false);
  
  const [productName, setProductName] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  
  const [wattages, setWattages] = useState([
    { watt: "", price: "" }
  ]);
  
  const [applications, setApplications] = useState([""]);
  
  // Technical Specifications with Key-Value pairs
  const [technicalSpecs, setTechnicalSpecs] = useState([
    { key: "SKU", value: "" },
    { key: "Wattage", value: "" },
    { key: "Voltage", value: "" },
    { key: "Material", value: "" },
    { key: "Master Packing", value: "" },
    { key: "Size", value: "" },
    { key: "Fitting", value: "" }
  ]);

  useEffect(() => {
    if (isOpen) {
      if (editProductId) {
        const product = getProductById(editProductId);
        if (product) {
          setIsEditing(true);
          setProductName(product.productName || "");
          setDescription(product.description || "");
          setImages(product.images || []);
          setWattages(product.wattages?.length ? product.wattages : [{ watt: "", price: "" }]);
          setApplications(product.applications?.length ? product.applications : [""]);
          
          // Handle technical specifications
          if (product.technicalSpecs && product.technicalSpecs.length) {
            setTechnicalSpecs(product.technicalSpecs);
          } else {
            setTechnicalSpecs([
              { key: "SKU", value: "" },
              { key: "Wattage", value: "" },
              { key: "Voltage", value: "" },
              { key: "Material", value: "" },
              { key: "Master Packing", value: "" },
              { key: "Size", value: "" },
              { key: "Fitting", value: "" }
            ]);
          }
        }
      } else {
        resetForm();
      }
    }
  }, [isOpen, editProductId]);

  if (!isOpen) return null;

  const resetForm = () => {
    setIsEditing(false);
    setProductName("");
    setDescription("");
    setImages([]);
    setWattages([{ watt: "", price: "" }]);
    setApplications([""]);
    setTechnicalSpecs([
      { key: "SKU", value: "" },
      { key: "Wattage", value: "" },
      { key: "Voltage", value: "" },
      { key: "Material", value: "" },
      { key: "Master Packing", value: "" },
      { key: "Size", value: "" },
      { key: "Fitting", value: "" }
    ]);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 6) {
      alert("You can only upload up to 6 images");
      return;
    }
    const newImages = files.map((file) => {
      const preview = URL.createObjectURL(file);
      return { file, preview };
    });
    setImages([...images, ...newImages]);
  };

  const removeImage = (index) => {
    const newImages = [...images];
    if (newImages[index].preview) {
      URL.revokeObjectURL(newImages[index].preview);
    }
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const updateWattage = (index, field, value) => {
    const newWattages = [...wattages];
    newWattages[index][field] = value;
    setWattages(newWattages);
  };

  const addWattage = () => {
    setWattages([...wattages, { watt: "", price: "" }]);
  };

  const removeWattage = (index) => {
    if (wattages.length > 1) {
      const newWattages = wattages.filter((_, i) => i !== index);
      setWattages(newWattages);
    }
  };

  const addApplication = () => setApplications([...applications, ""]);
  const removeApplication = (index) => {
    setApplications(applications.filter((_, i) => i !== index));
  };
  const updateApplication = (index, value) => {
    const newApplications = [...applications];
    newApplications[index] = value;
    setApplications(newApplications);
  };

  // Technical Specifications handlers
  const updateTechnicalSpec = (index, field, value) => {
    const newTechnicalSpecs = [...technicalSpecs];
    newTechnicalSpecs[index][field] = value;
    setTechnicalSpecs(newTechnicalSpecs);
  };

  const addTechnicalSpec = () => {
    setTechnicalSpecs([...technicalSpecs, { key: "", value: "" }]);
  };

  const removeTechnicalSpec = (index) => {
    if (technicalSpecs.length > 1) {
      const newTechnicalSpecs = technicalSpecs.filter((_, i) => i !== index);
      setTechnicalSpecs(newTechnicalSpecs);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validWattages = wattages.filter(w => w.watt.trim() !== "" && w.price.trim() !== "");
    const validApplications = applications.filter(a => a.trim() !== "");
    const validTechnicalSpecs = technicalSpecs.filter(spec => spec.key.trim() !== "" && spec.value.trim() !== "");
    
    if (validWattages.length === 0) {
      alert("Please add at least one wattage and price");
      return;
    }
    
    // Process images
    const processedImages = images.map(img => img.preview || img);
    
    const formData = {
      productName: productName.trim(),
      description: description.trim(),
      images: processedImages,
      wattages: validWattages,
      applications: validApplications,
      technicalSpecs: validTechnicalSpecs,
    };
    
    if (isEditing && editProductId) {
      updateProduct(editProductId, formData);
      alert("Product updated successfully!");
    } else {
      addProduct(formData);
      alert("Product added successfully!");
    }
    
    resetForm();
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {isEditing ? <FaEdit /> : <FaTag />}
            {isEditing ? " Edit Product" : " Add New Product"}
          </h2>
          <button type="button" className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="product-form">
          {/* Product Name */}
          <div className="form-group">
            <label>Product Name *</label>
            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="Enter product name"
              required
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter product description"
              rows="4"
            />
          </div>

          {/* Wattages and Prices */}
          <div className="form-group">
            <label>Wattages & Prices</label>
            {wattages.map((wattage, index) => (
              <div key={index} className="wattage-row">
                <input
                  type="text"
                  placeholder="Wattage (e.g., 100W)"
                  value={wattage.watt}
                  onChange={(e) => updateWattage(index, "watt", e.target.value)}
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Price"
                  value={wattage.price}
                  onChange={(e) => updateWattage(index, "price", e.target.value)}
                />
                {wattages.length > 1 && (
                  <button type="button" className="remove-btn" onClick={() => removeWattage(index)}>
                    <FaTrash />
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="add-btn" onClick={addWattage}>
              <FaPlus /> Add Wattage
            </button>
          </div>

          {/* Images Upload */}
          <div className="form-group">
            <label>Product Images (Max 6)</label>
            <div className="image-upload-area">
              <label className="upload-label">
                <FaUpload /> Choose Images
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  hidden
                />
              </label>
              {images.length > 0 && (
                <div className="image-preview-grid">
                  {images.map((img, idx) => (
                    <div key={idx} className="image-preview">
                      <img src={img.preview || img} alt={`Preview ${idx}`} />
                      <button type="button" className="remove-image" onClick={() => removeImage(idx)}>
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Applications */}
          <div className="form-group">
            <label>Applications</label>
            {applications.map((app, idx) => (
              <div key={idx} className="dynamic-item">
                <input
                  type="text"
                  value={app}
                  onChange={(e) => updateApplication(idx, e.target.value)}
                  placeholder={`Application ${idx + 1}`}
                />
                {applications.length > 1 && (
                  <button type="button" className="remove-btn" onClick={() => removeApplication(idx)}>
                    <FaTrash />
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="add-btn" onClick={addApplication}>
              <FaPlus /> Add Application
            </button>
          </div>

          {/* Technical Specifications - Two Column Table */}
          <div className="form-group">
            <label className="specs-label">
              <FaCog /> Technical Specifications
            </label>
            <div className="technical-specs-table">
              <div className="specs-header">
                <span>Specification</span>
                <span>Value</span>
                <span></span>
              </div>
              {technicalSpecs.map((spec, index) => (
                <div key={index} className="specs-row">
                  <input
                    type="text"
                    placeholder="e.g., SKU, Wattage, Voltage"
                    value={spec.key}
                    onChange={(e) => updateTechnicalSpec(index, "key", e.target.value)}
                    className="specs-key-input"
                  />
                  <input
                    type="text"
                    placeholder="e.g., 1000W, 220v, Stainless Steel"
                    value={spec.value}
                    onChange={(e) => updateTechnicalSpec(index, "value", e.target.value)}
                    className="specs-value-input"
                  />
                  <button 
                    type="button" 
                    className="remove-specs-btn" 
                    onClick={() => removeTechnicalSpec(index)}
                    title="Remove"
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="add-specs-btn" onClick={addTechnicalSpec}>
              <FaPlusCircle /> Add Specification
            </button>
            <div className="specs-example">
              <small>Example: SKU | Wattage | Voltage | Material | Master Packing | Size | Fitting</small>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submit-btn">
              {isEditing ? "Update Product" : "Add Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProductFormModal;